import { useCallback, useEffect, useMemo, useState } from 'react'
import { organizationService } from '../services'
import { useDebouncedValue } from './useDebouncedValue'

const SEARCH_DEBOUNCE_MS = 400
const DEFAULT_LIMIT = 25

function orgKey(org) {
  return String(org?.id ?? '')
}

function mergeSelectedIntoOptions(options, selected) {
  const byId = new Map()
  for (const org of options || []) {
    if (org?.id == null) continue
    byId.set(orgKey(org), org)
  }
  for (const org of selected || []) {
    if (org?.id == null) continue
    if (!byId.has(orgKey(org))) byId.set(orgKey(org), org)
  }
  return Array.from(byId.values())
}

/**
 * Multi-select shop picker backed by GET /admin/organizations?search=
 * (name ILIKE or id LIKE). Avoids the old "first 100 only" Autocomplete trap.
 */
export function useOrganizationMultiSearch({
  enabled = true,
  limit = DEFAULT_LIMIT,
  selectedIds = null,
} = {}) {
  const [searchText, setSearchText] = useState('')
  const debouncedSearch = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS)
  const [fetchedOptions, setFetchedOptions] = useState([])
  const [selectedOrgs, setSelectedOrgs] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const trimmed = debouncedSearch.trim()
        const { items } = await organizationService.getOrganizationsPage({
          page: 1,
          limit,
          search: trimmed || undefined,
        })
        if (!cancelled) setFetchedOptions(Array.isArray(items) ? items : [])
      } catch (err) {
        console.error('Failed to search organizations', err)
        if (!cancelled) setFetchedOptions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [debouncedSearch, enabled, limit])

  const selectedIdsKey = Array.isArray(selectedIds)
    ? selectedIds.map(String).join(',')
    : ''

  // Hydrate selection when parent drives ids (e.g. URL filters) and we lack objects.
  useEffect(() => {
    if (!enabled || !Array.isArray(selectedIds)) return undefined
    if (selectedIds.length === 0) {
      setSelectedOrgs((prev) => (prev.length === 0 ? prev : []))
      return undefined
    }

    let cancelled = false
    ;(async () => {
      try {
        const results = await Promise.all(
          selectedIds.map((id) =>
            organizationService.getOrganizationsPage({
              page: 1,
              limit: 5,
              search: String(id),
            }),
          ),
        )
        if (cancelled) return
        const found = []
        selectedIds.forEach((id, index) => {
          const match = (results[index]?.items || []).find(
            (org) => String(org.id) === String(id),
          )
          if (match) found.push(match)
        })
        setSelectedOrgs(found)
      } catch (err) {
        console.error('Failed to hydrate selected organizations', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [enabled, selectedIds, selectedIdsKey])

  const options = useMemo(
    () => mergeSelectedIntoOptions(fetchedOptions, selectedOrgs),
    [fetchedOptions, selectedOrgs],
  )

  const setSelectedFromOrgs = useCallback((orgs) => {
    const next = Array.isArray(orgs) ? orgs : []
    setSelectedOrgs(next)
    return next.map((org) => org.id)
  }, [])

  const selectAllVisible = useCallback(() => {
    const next = mergeSelectedIntoOptions(selectedOrgs, fetchedOptions)
    setSelectedOrgs(next)
    return next.map((org) => org.id)
  }, [fetchedOptions, selectedOrgs])

  const clearSelected = useCallback(() => {
    setSelectedOrgs([])
    return []
  }, [])

  const resetSearch = useCallback(() => {
    setSearchText('')
  }, [])

  return {
    searchText,
    setSearchText,
    options,
    loading,
    selectedOrgs,
    setSelectedFromOrgs,
    selectAllVisible,
    clearSelected,
    resetSearch,
  }
}
